"use client";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  type FC,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import * as Styled from "./BlurContainer.styles";

export interface BlurContainerProps {
  isMetricsLine?: boolean;
  children?: ReactNode;
}

const SCROLL_EDGE_THRESHOLD = 6;

export const BlurContainer: FC<BlurContainerProps> = ({
  children,
  isMetricsLine = false,
}) => {
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const updateScrollButtons = useCallback(() => {
    const target = scrollRef.current;
    if (!target) {
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = target;
    const hasOverflow = scrollWidth > clientWidth + 1;

    if (!hasOverflow) {
      setShowLeftButton(false);
      setShowRightButton(false);
      return;
    }

    setShowLeftButton(scrollLeft > SCROLL_EDGE_THRESHOLD);
    setShowRightButton(
      scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_THRESHOLD
    );
  }, []);

  useEffect(() => {
    const target = scrollRef.current;
    if (!target) {
      return;
    }

    updateScrollButtons();

    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(target);

    const content = target.firstElementChild;
    if (content) {
      resizeObserver.observe(content);
    }

    target.addEventListener("scrollend", updateScrollButtons);

    return () => {
      resizeObserver.disconnect();
      target.removeEventListener("scrollend", updateScrollButtons);
    };
  }, [updateScrollButtons]);

  const onScroll = (event: SyntheticEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const { scrollLeft, clientWidth, scrollWidth } = target;
    const hasOverflow = scrollWidth > clientWidth + 1;

    if (!hasOverflow) {
      setShowLeftButton(false);
      setShowRightButton(false);
      return;
    }

    setShowLeftButton(scrollLeft > SCROLL_EDGE_THRESHOLD);
    setShowRightButton(
      scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_THRESHOLD
    );
  };

  const scrollToEdge = useCallback(
    (direction: "left" | "right") => {
      flushSync(() => {
        if (direction === "right") {
          setShowRightButton(false);
          return;
        }

        setShowLeftButton(false);
      });

      const target = scrollRef.current;
      if (!target) {
        return;
      }

      const maxScrollLeft = Math.max(
        0,
        target.scrollWidth - target.clientWidth
      );

      target.scrollTo({
        left: direction === "left" ? 0 : maxScrollLeft,
        behavior: "auto",
      });

      updateScrollButtons();
    },
    [updateScrollButtons]
  );

  return (
    <Styled.Root $isMetricsLine={isMetricsLine}>
      {showLeftButton && (
        <Styled.ScrollArrow className="left" onClick={() => scrollToEdge("left")}>
          <ChevronLeftIcon />
        </Styled.ScrollArrow>
      )}
      <Styled.ScrollContainer
        ref={scrollRef}
        onScroll={onScroll}
        $paddingLeft={showLeftButton}
        $paddingRight={showRightButton}
      >
        {children}
      </Styled.ScrollContainer>
      {showRightButton && (
        <Styled.ScrollArrow className="right" onClick={() => scrollToEdge("right")}>
          <ChevronRightIcon />
        </Styled.ScrollArrow>
      )}
    </Styled.Root>
  );
};
